import React, { useState, useEffect, FC, MouseEvent, ChangeEvent, KeyboardEvent } from 'react';
import { Home, Shield, BookOpen, Users, Phone, PlusCircle, ArrowLeft, HeartPulse, Stethoscope, Syringe, Pill, AlertTriangle, User, Sparkles, Send, LucideProps, Loader, Droplet, Thermometer, Wind, Zap, Bone, BrainCircuit, Activity, Sun, Eye, ShoppingCart, ClipboardList, Search, UtensilsCrossed, FileText } from 'lucide-react';

// --- DEFINICIONES DE TIPOS (TypeScript) ---

interface Subsection {
  title: string;
  steps: string[];
}

interface Guide {
  id: string;
  title: string;
  category: 'emergency' | 'firstAid';
  iconName: string; 
  interactive: boolean;
  content?: Subsection; 
  subsections?: { [key: string]: Subsection };
}

interface VaccineMilestone {
  edad: string;
  vacunas: string[];
}

interface Member {
  id: number;
  name: string;
  dob: string;
  photo?: string;
  bloodType?: string;
  allergies?: string;
  medications?: string;
}

interface FirstAidKitItem {
    name: string;
    description: string;
    checked: boolean;
}

type Screen = 'home' | 'emergency' | 'firstAid' | 'profiles' | 'editProfile' | 'aiAssistant' | 'firstAidKit' | 'symptomChecker' | 'prospectusInterpreter' | 'mealPlanner';

// --- MAPA DE ICONOS ---
const iconMap: { [key:string]: FC<LucideProps> } = {
    AlertTriangle, HeartPulse, Shield, Droplet, Zap, Bone, BrainCircuit, Activity, Stethoscope, Thermometer, Sun, Eye, Wind, Pill,
};

// --- DATOS ESTÁTICOS DE LA APP ---

const initialFirstAidKit: FirstAidKitItem[] = [
    { name: "Gasas estériles", description: "Para limpiar heridas y cubrir cortes.", checked: false },
    { name: "Esparadrapo hipoalergénico", description: "Para sujetar gasas y vendajes.", checked: false },
    { name: "Tiritas (varios tamaños)", description: "Para cortes y rasguños pequeños.", checked: false },
    { name: "Antiséptico", description: "Povidona yodada o clorhexidina para desinfectar.", checked: false },
    { name: "Suero fisiológico", description: "Para limpiar heridas o lavar los ojos.", checked: false },
    { name: "Tijeras de punta redonda", description: "Para cortar gasas o ropa.", checked: false },
    { name: "Pinzas finas", description: "Para extraer astillas o pequeños objetos.", checked: false },
    { name: "Termómetro digital", description: "Para medir la fiebre con precisión.", checked: false },
    { name: "Analgésicos y Antitérmicos", description: "Paracetamol e Ibuprofeno (dosis pediátricas y de adulto).", checked: false },
    { name: "Crema para quemaduras", description: "Específica para quemaduras solares o leves.", checked: false },
    { name: "Vendas elásticas", description: "Para esguinces o para sujetar apósitos.", checked: false },
    { name: "Guantes desechables", description: "Para protegerse al curar heridas.", checked: false },
];

const guiasEmergencia: Guide[] = [
    {
        id: "atragantamiento", title: "Atragantamiento", iconName: "AlertTriangle", category: 'emergency', interactive: true,
        subsections: {
            "Bebé (menor de 1 año)": { title: "Pasos para Bebé", steps: ["Sostén al bebé boca abajo sobre tu antebrazo, apoyando su cabeza.", "Dale 5 golpes suaves pero firmes en la espalda con la base de la mano.", "Si no funciona, voltéalo boca arriba y realiza 5 compresiones en el pecho con dos dedos.", "Alterna hasta que el objeto salga o llame a emergencias (112)."] },
            "Niño / Adulto": { title: "Pasos para Niño/Adulto", steps: ["Anima a la persona a toser con fuerza.", "Si no puede, dale 5 golpes secos en la espalda, entre los omóplatos.", "Realiza 5 compresiones abdominales (Maniobra de Heimlich).", "Alterna 5 golpes y 5 compresiones hasta que el objeto salga o la persona pierda el conocimiento."] }
        }
    },
    {
        id: "rcp", title: "RCP (Reanimación)", iconName: "HeartPulse", category: 'emergency', interactive: false,
        content: { title: "Pasos Críticos de RCP", steps: ["Verifica la seguridad del entorno.", "Comprueba si la persona responde. Llama a emergencias (112).", "Abre la vía aérea (inclina la cabeza, eleva el mentón).", "Comprueba si respira (no más de 10 seg).", "Inicia 30 compresiones torácicas fuertes y rápidas en el centro del pecho.", "Si estás entrenado, realiza 2 insuflaciones.", "Continúa el ciclo 30:2 hasta que llegue la ayuda."] }
    },
    {
        id: "quemaduras", title: "Quemaduras", iconName: "Shield", category: 'emergency', interactive: true,
        subsections: {
            "Quemadura Leve (1er grado)": { title: "Actuación en Quemaduras Leves", steps: ["Enfría la zona con agua corriente fría (no helada) durante 10-20 minutos.", "Cubre la quemadura con una gasa estéril o un paño limpio.", "No apliques hielo, mantequilla ni pomadas.", "No rompas las ampollas si aparecen."] },
            "Quemadura Grave (2º/3er grado)": { title: "Actuación en Quemaduras Graves", steps: ["Llama a emergencias (112) inmediatamente.", "No retires ropa pegada a la piel.", "Cubre la zona con un paño limpio y húmedo hasta que llegue la ayuda.", "Vigila signos de shock (palidez, sudor frío)."] }
        }
    },
    { id: "sangrado", title: "Hemorragia Grave", iconName: "Droplet", category: 'emergency', interactive: false, content: { title: "Control de Hemorragia", steps: ["Llama a emergencias (112).", "Aplica presión directa y fuerte sobre la herida con un paño limpio o gasa.", "Si es posible, eleva la extremidad afectada por encima del nivel del corazón.", "Si la sangre empapa la gasa, no la retires. Añade más gasas encima y sigue presionando.", "No apliques un torniquete a menos que estés entrenado y sea la última opción."] } },
    { id: "anafilaxia", title: "Reacción Alérgica Grave", iconName: "Wind", category: 'emergency', interactive: false, content: { title: "Actuación ante Anafilaxia", steps: ["Llama a emergencias (112) de inmediato.", "Si la persona tiene un autoinyector de adrenalina (ej. EpiPen), ayúdale a usarlo.", "Acuéstala con las piernas elevadas.", "Afloja cualquier prenda de ropa ajustada.", "Si vomita o sangra por la boca, gírala de lado."] } },
    { id: "convulsiones", title: "Convulsiones", iconName: "Zap", category: 'emergency', interactive: false, content: { title: "Manejo de Convulsiones", steps: ["Mantén la calma. Aleja objetos peligrosos de la persona.", "Coloca algo blando debajo de su cabeza (una chaqueta, cojin).", "Gira a la persona de lado para ayudarla a respirar.", "No intentes sujetarla ni ponerle nada en la boca.", "Cronometra la duración. Si dura más de 5 minutos, llama al 112."] } },
    { id: "golpe_cabeza", title: "Golpe en la Cabeza", iconName: "BrainCircuit", category: 'emergency', interactive: true, subsections: { "Señales de Alarma (Ir a Urgencias)": {title: "Cuándo ir a Urgencias", steps: ["Pérdida de conocimiento, aunque sea breve.", "Vómitos repetidos.", "Fuerte dolor de cabeza que no cede.", "Confusión, somnolencia inusual o cambios de comportamiento.", "Convulsiones.", "Debilidad o entumecimiento en alguna parte del cuerpo."]}, "Manejo en Casa (Golpe Leve)": {title: "Manejo de un Golpe Leve", steps: ["Aplica hielo o una compresa fría en la zona durante 15-20 minutos.", "Observa a la persona durante las siguientes horas.", "Permite que descanse, pero despiértala cada 2-3 horas durante la noche para comprobar que responde con normalidad.", "Si aparece cualquier señal de alarma, acude a urgencias."]}}},
    { id: "intoxicacion", title: "Intoxicación", iconName: "Pill", category: 'emergency', interactive: false, content: { title: "Actuación ante Intoxicación", steps: ["Llama al Instituto Nacional de Toxicología (consulta el número local) o a emergencias (112).", "No provoques el vómito a menos que te lo indiquen.", "Intenta identificar qué ha ingerido la persona, la cantidad y cuándo.", "Guarda el envase del producto para mostrarlo al personal sanitario."] } },
    { id: "fractura_abierta", title: "Fractura Abierta", iconName: "Bone", category: 'emergency', interactive: false, content: { title: "Manejo de Fractura Abierta", steps: ["Llama a emergencias (112) inmediatamente.", "No intentes mover a la persona ni recolocar el hueso.", "Cubre la herida con una gasa estéril o un paño lo más limpio posible.", "Controla cualquier sangrado aplicando presión alrededor de la herida, no directamente sobre el hueso.", "Inmoviliza la zona como puedas, sin moverla bruscamente."] } },
    { id: "shock_electrico", title: "Shock Eléctrico", iconName: "Activity", category: 'emergency', interactive: false, content: { title: "Actuación ante Shock Eléctrico", steps: ["No toques a la persona si sigue en contacto con la fuente eléctrica.", "Desconecta la corriente eléctrica si es posible y seguro hacerlo.", "Si no puedes, usa un objeto no conductor (madera, plástico) para apartar la fuente eléctrica de la persona.", "Una vez sea seguro, comprueba si respira y tiene pulso. Llama al 112 e inicia RCP si es necesario."] } },
];

const guiasPrimerosAuxilios: Guide[] = [
    { id: "cortes_leves", title: "Cortes y Heridas Leves", iconName: "Stethoscope", category: 'firstAid', interactive: false, content: { title: "Cuidado de Heridas Leves", steps: ["Lávate las manos con agua y jabón.", "Limpia la herida con agua corriente y jabón neutro.", "Aplica presión suave con una gasa limpia si sangra un poco.", "Aplica un antiséptico (povidona yodada, clorhexidina).", "Cubre la herida con un apósito o tirita."] } },
    { id: "picaduras", title: "Picaduras de Insectos", iconName: "Stethoscope", category: 'firstAid', interactive: false, content: { title: "Alivio de Picaduras", steps: ["Si hay aguijón, retíralo raspando con una tarjeta (no uses pinzas).", "Lava la zona con agua y jabón.", "Aplica una compresa fría para reducir la hinchazón.", "Usa una crema con hidrocortisona o calamina para la picazón.", "Vigila signos de reacción alérgica grave (dificultad para respirar)."] } },
    { id: "fiebre", title: "Manejo de la Fiebre", iconName: "Thermometer", category: 'firstAid', interactive: true, subsections: { "Bebés (0-3 meses)": {title: "Fiebre en Neonatos", steps: ["Cualquier fiebre (+38°C rectal) en un bebé menor de 3 meses es una urgencia. Acude al médico de inmediato.", "No le des medicación sin consultar antes."]}, "Niños (mayores de 3 meses)": {title: "Fiebre en Niños", steps: ["Mantén al niño hidratado con líquidos.", "Vístelo con ropa ligera.", "Puedes darle un baño con agua tibia (no fría).", "Administra un antitérmico (paracetamol o ibuprofeno) según la dosis recomendada por peso."]}, "Adultos": {title: "Fiebre en Adultos", steps: ["Descansa y bebe abundantes líquidos.", "Toma paracetamol o ibuprofeno para aliviar el malestar.", "Consulta al médico si la fiebre es muy alta, dura más de 3 días o se acompaña de otros síntomas graves."]} } },
    { id: "quemadura_solar", title: "Quemadura Solar", iconName: "Sun", category: 'firstAid', interactive: false, content: { title: "Alivio de Quemaduras Solares", steps: ["Refréscate con duchas o paños de agua fría.", "Aplica gel de aloe vera o una loción post-solar para calmar la piel.", "Bebe mucha agua para evitar la deshidratación.", "Si aparecen ampollas, no las rompas.", "Evita volver a exponerte al sol hasta que la piel se recupere."] } },
    { id: "esguince", title: "Esguince o Torcedura", iconName: "Bone", category: 'firstAid', interactive: false, content: { title: "Método R.I.C.E.", steps: ["**Reposo:** Evita apoyar o mover la articulación afectada.", "**Hielo (Ice):** Aplica hielo envuelto en un paño durante 15-20 minutos, varias veces al día.", "**Compresión:** Venda la zona con una venda elástica sin apretar demasiado.", "**Elevación:** Mantén la extremidad elevada por encima del nivel del corazón."] } },
    { id: "sangrado_nariz", title: "Hemorragia Nasal", iconName: "Droplet", category: 'firstAid', interactive: false, content: { title: "Cómo detenerla", steps: ["Siéntate e inclina la cabeza ligeramente hacia ADELANTE (no hacia atrás).", "Presiona firmemente la parte blanda de la nariz (justo debajo del puente óseo) con los dedos pulgar e índice.", "Mantén la presión de forma continua durante al menos 10-15 minutos.", "Respira por la boca.", "Si no se detiene en 20 minutos, busca atención médica."] } },
    { id: "ojo_objeto", title: "Objeto en el Ojo", iconName: "Eye", category: 'firstAid', interactive: false, content: { title: "Cómo actuar", steps: ["Lávate las manos antes de tocar el ojo.", "Intenta que la persona parpadee varias veces para que la lágrima expulse el objeto.", "Lava el ojo con abundante agua limpia o suero fisiológico.", "No intentes extraer un objeto que esté clavado. Cubre el ojo sin presionar y acude a urgencias.", "No frotes el ojo."] } },
    { id: "desmayo", title: "Desmayo o Lipotimia", iconName: "Wind", category: 'firstAid', interactive: false, content: { title: "Actuación ante un Desmayo", steps: ["Acuésta a la persona en el suelo boca arriba.", "Elévale las piernas unos 30 cm por encima del nivel del corazón.", "Afloja cualquier prenda de ropa ajustada (cinturones, cuellos).", "Asegúrate de que haya buena ventilación.", "La persona debería recuperarse en menos de un minuto. Si no es así, llama al 112."] } },
    { id: "ampollas", title: "Cuidado de Ampollas", iconName: "Stethoscope", category: 'firstAid', interactive: false, content: { title: "Tratamiento de Ampollas", steps: ["No la rompas. La piel protege contra infecciones.", "Lava la zona con agua y jabón.", "Cubre la ampolla con un apósito especial para ampollas o una gasa para protegerla del roce.", "Si se rompe sola, limpia la zona, aplica un antiséptico y cúbrela."] } },
    { id: "dolor_muelas", title: "Dolor de Muelas", iconName: "Stethoscope", category: 'firstAid', interactive: false, content: { title: "Alivio Temporal", steps: ["Enjuágate la boca con agua tibia y sal.", "Usa hilo dental con cuidado para eliminar restos de comida.", "Aplica una compresa fría en la mejilla para reducir la inflamación.", "Toma un analgésico de venta libre si es necesario.", "Pide cita con tu dentista lo antes posible."] } },
];

const calendarioVacunasEspaña: VaccineMilestone[] = [
    { edad: "2 meses", vacunas: ["Hexavalente (1ª)", "Neumococo (1ª)", "Rotavirus (1ª)*", "Meningococo B (1ª)*"] },
    { edad: "4 meses", vacunas: ["Hexavalente (2ª)", "Neumococo (2ª)", "Rotavirus (2ª)*", "Meningococo B (2ª)*"] },
    { edad: "11 meses", vacunas: ["Hexavalente (3ª)", "Neumococo (3ª)"] },
    { edad: "12 meses", vacunas: ["Triple Vírica (1ª)", "Meningococo C"] },
    { edad: "15 meses", vacunas: ["Varicela (1ª)", "Meningococo B (refuerzo)*"] },
    { edad: "3-4 años", vacunas: ["Triple Vírica (2ª)", "DTPa-VPI (refuerzo)", "Varicela (2ª)"] },
    { edad: "6 años", vacunas: ["dTpa (refuerzo)"] },
    { edad: "12 años", vacunas: ["VPH (2 dosis)", "Meningococo ACWY"] },
    { edad: "14 años", vacunas: ["Td (refuerzo)"] },
];

// --- COMPONENTES Y PANTALLAS ---

const Header: FC<{ title: string; onBack?: () => void }> = ({ title, onBack }) => (
  <header className="p-4 flex items-center bg-white shadow-sm sticky top-0 z-20">
    {onBack && (<button onClick={onBack} className="mr-4 p-2 rounded-full hover:bg-gray-200"><ArrowLeft className="w-6 h-6 text-gray-600" /></button>)}
    <h1 className="text-xl font-semibold text-gray-700">{title}</h1>
  </header>
);

const IconoBoton: FC<{ icon: FC<LucideProps>; label: string; onClick: () => void; className?: string }> = ({ icon: Icon, label, onClick, className = "" }) => (
  <button onClick={onClick} className={`flex flex-col items-center justify-center space-y-2 p-3 md:p-4 rounded-2xl text-center transition-all transform hover:scale-105 hover:shadow-lg bg-white border ${className}`}>
    <div className="p-3 bg-[#00a9a9]/10 rounded-full">
      <Icon className="w-8 h-8 text-[#00a9a9]" />
    </div>
    <span className="font-semibold text-sm md:text-base text-gray-700">{label}</span>
  </button>
);

const TarjetaGuia: FC<{ title: string; icon: FC<LucideProps>; onClick: () => void }> = ({ title, icon: Icon, onClick }) => (
  <div onClick={onClick} className="bg-white p-4 rounded-xl flex items-center space-x-4 cursor-pointer hover:bg-gray-50 transition-colors shadow-md border">
    <Icon className="w-7 h-7 text-[#00a9a9] flex-shrink-0" />
    <span className="font-semibold text-gray-800">{title}</span>
  </div>
);

const BottomNav: FC<{ screen: Screen; setScreen: (screen: Screen) => void }> = ({ screen, setScreen }) => (
    <nav className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-sm flex justify-around p-2 border-t border-gray-200 z-20">
        <button onClick={() => setScreen('home')} className={`flex flex-col items-center p-2 rounded-lg ${screen === 'home' ? 'text-[#00a9a9]' : 'text-gray-500'}`}><Home /><span className="text-xs font-medium">Inicio</span></button>
        <button onClick={() => setScreen('profiles')} className={`flex flex-col items-center p-2 rounded-lg ${['profiles', 'editProfile'].includes(screen) ? 'text-[#00a9a9]' : 'text-gray-500'}`}><Users /><span className="text-xs font-medium">Perfiles</span></button>
        <button onClick={() => setScreen('aiAssistant')} className={`flex flex-col items-center p-2 rounded-lg ${screen === 'aiAssistant' ? 'text-[#00a9a9]' : 'text-gray-500'}`}><Sparkles /><span className="text-xs font-medium">Asistente IA</span></button>
    </nav>
);

const HomeScreen: FC<{ navigate: (screen: Screen) => void }> = ({ navigate }) => (
  <div className="p-4 space-y-6">
    <div className="text-center p-6 bg-[#ffeded] rounded-2xl shadow-sm">
      <img src="https://paraelbebe.com/wp-content/uploads/2022/03/Para-el-bebe%CC%81-headers-4.png" alt="Logo ParaElBebe.com" className="mx-auto h-12 mb-4" />
      <h2 className="text-2xl font-bold text-gray-800">Asistente de Salud Familiar</h2>
      <p className="mt-2 text-gray-600">Tu guía rápida para emergencias y gestión de la salud de tu familia.</p>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <IconoBoton icon={AlertTriangle} label="Emergencias" onClick={() => navigate('emergency')} />
      <IconoBoton icon={BookOpen} label="Primeros Auxilios" onClick={() => navigate('firstAid')} />
      <IconoBoton icon={ClipboardList} label="Checklist Botiquín" onClick={() => navigate('firstAidKit')} />
      <IconoBoton icon={Search} label="Analizador Síntomas" onClick={() => navigate('symptomChecker')} />
      <IconoBoton icon={FileText} label="Intérprete Prospectos" onClick={() => navigate('prospectusInterpreter')} />
      <IconoBoton icon={UtensilsCrossed} label="Planificador de Menús" onClick={() => navigate('mealPlanner')} />
    </div>
  </div>
);

const GuideListScreen: FC<{ title: string; guides: Guide[]; isLoading: boolean; onSelect: (guide: Guide) => void; onBack: () => void }> = ({ title, guides, isLoading, onSelect, onBack }) => (
  <div><Header title={title} onBack={onBack} /><div className="p-4 space-y-4">
    {isLoading ? (<div className="flex justify-center items-center p-10"><Loader className="animate-spin w-10 h-10 text-[#00a9a9]" /></div>) 
    : guides.length === 0 ? (<div className="text-center p-6 text-gray-500">No se encontraron guías en esta categoría.</div>)
    : (guides.map(guide => {
        const IconComponent = iconMap[guide.iconName] || Stethoscope;
        return <TarjetaGuia key={guide.id} title={guide.title} icon={IconComponent} onClick={() => onSelect(guide)} />
    }))}
  </div></div>
);

const GuideDetailScreen: FC<{ title: string; content: Subsection; onBack: () => void; onAmplifyWithAI: (context: string) => void; }> = ({ title, content, onBack, onAmplifyWithAI }) => {
    const [products, setProducts] = useState<string[]>([]);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);

    const handleFetchProducts = async () => {
        setIsLoadingProducts(true);
        const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
        if (!apiKey) {
            console.error("API Key not found");
            setIsLoadingProducts(false);
            return;
        }

        const prompt = `Para una situación de "${title}", sugiere 2 o 3 productos de primeros auxilios relevantes que se pueden encontrar en Amazon. Devuelve únicamente un array JSON con los términos de búsqueda. Ejemplo: ["gasas estériles", "crema para quemaduras solares"]`;
        
        const payload = { contents: [{ role: "user", parts: [{ text: prompt }] }] };
        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

        try {
            const res = await fetch(apiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const data = await res.json();
            const text = data.candidates[0].content.parts[0].text;
            const jsonString = text.substring(text.indexOf('['), text.lastIndexOf(']') + 1);
            setProducts(JSON.parse(jsonString));
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setIsLoadingProducts(false);
        }
    };

    return (
    <div><Header title={title} onBack={onBack} /><div className="p-4 space-y-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border"><h3 className="text-lg font-bold text-[#00a9a9] mb-3">{content.title}</h3><ul className="space-y-3 list-inside text-gray-700">{content.steps.map((step, index) => (<li key={index} className="flex items-start"><span className="text-[#00a9a9] font-bold mr-3">{index + 1}.</span><span dangerouslySetInnerHTML={{ __html: step.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}></span></li>))}</ul></div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-lg font-bold text-[#00a9a9] mb-3">Productos Recomendados</h3>
            {products.length === 0 && !isLoadingProducts && (
                 <button onClick={handleFetchProducts} className="w-full mt-2 flex items-center justify-center p-3 bg-amber-400 hover:bg-amber-500 text-amber-900 rounded-xl font-bold text-base transition-colors shadow-lg"><ShoppingCart className="mr-2 h-5 w-5" />Ver productos sugeridos</button>
            )}
            {isLoadingProducts && <div className="text-center p-4"><Loader className="animate-spin w-6 h-6 mx-auto text-[#00a9a9]" /></div>}
            {products.length > 0 && (
                <div className="space-y-2">
                    {products.map((product, index) => (
                        <a key={index} href={`https://www.amazon.es/s?k=${encodeURIComponent(product)}&tag=paraelbebe-21`} target="_blank" rel="noopener noreferrer" className="block p-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-center font-semibold text-gray-800">
                            Buscar "{product}" en Amazon
                        </a>
                    ))}
                    <p className="text-xs text-center text-gray-500 pt-2">Como afiliado de Amazon, podemos recibir una comisión por compras cualificadas.</p>
                </div>
            )}
        </div>

        <button onClick={() => onAmplifyWithAI(title)} className="w-full mt-4 flex items-center justify-center p-3 bg-[#00a9a9] hover:bg-[#008f8f] text-white rounded-xl font-bold text-base transition-colors shadow-lg"><Sparkles className="mr-2 h-5 w-5" />Ampliar información con IA</button>
        <div className="mt-6 p-3 bg-red-100 text-red-800 rounded-lg text-center"><p className="font-semibold">Esta es una guía rápida. En caso de duda o emergencia grave, llama siempre al 112.</p></div>
    </div></div>
    );
};

const SubsectionsScreen: FC<{ guide: Guide; onSelect: (subsection: Subsection, title: string) => void; onBack: () => void; }> = ({ guide, onSelect, onBack }) => (
    <div>
        <Header title={guide.title} onBack={onBack} />
        <div className="p-4 space-y-4">
            <div className="text-center text-lg p-2 text-gray-600">Selecciona una opción:</div>
            {guide.subsections && Object.entries(guide.subsections).map(([key, subsection]) => (
                <button key={key} onClick={() => onSelect(subsection, `${guide.title}: ${key}`)} className="w-full text-left bg-white p-4 rounded-xl flex items-center space-x-4 cursor-pointer hover:bg-gray-50 transition-colors shadow-md border">
                    <span className="font-semibold text-lg text-gray-800">{key}</span>
                </button>
            ))}
        </div>
    </div>
);

const ProfilesScreen: FC<{ members: Member[]; onSelect: (member: Member) => void; onAdd: () => void; onEdit: (member: Member) => void; onBack: () => void }> = ({ members, onSelect, onAdd, onEdit, onBack }) => (
    <div><Header title="Perfiles Familiares" onBack={onBack} /><div className="p-4 space-y-4">{members.length > 0 ? (members.map(member => (<div key={member.id} onClick={() => onSelect(member)} className="bg-white p-4 rounded-xl flex items-center space-x-4 cursor-pointer hover:bg-gray-50 transition-colors shadow-md border"><img src={member.photo || `https://placehold.co/60x60/e2e8f0/334155?text=${member.name.charAt(0)}`} alt={member.name} className="w-14 h-14 rounded-full object-cover border-2 border-[#00a9a9]"/><div className="flex-grow"><p className="font-bold text-lg text-gray-800">{member.name}</p><p className="text-sm text-gray-500">{member.dob}</p></div><button onClick={(e: MouseEvent) => { e.stopPropagation(); onEdit(member); }} className="text-[#00a9a9] hover:text-[#008f8f] p-2 rounded-full font-semibold">Editar</button></div>))) : (<div className="text-center py-10 px-4 bg-gray-50 rounded-lg"><p className="text-gray-500">No has añadido ningún miembro a tu familia.</p></div>)}<button onClick={onAdd} className="w-full flex items-center justify-center p-4 bg-[#00a9a9] hover:bg-[#008f8f] text-white rounded-xl font-bold text-lg transition-colors shadow-lg"><PlusCircle className="mr-2" />Añadir Miembro</button></div></div>
);

const ProfileDetailScreen: FC<{ member: Member; onBack: () => void; calendario: VaccineMilestone[] }> = ({ member, onBack, calendario }) => (
    <div><Header title={member.name} onBack={onBack} /><div className="p-4 space-y-6"><div className="flex flex-col items-center space-y-4 bg-white p-6 rounded-2xl shadow-sm border"><img src={member.photo || `https://placehold.co/100x100/e2e8f0/334155?text=${member.name.charAt(0)}`} alt={member.name} className="w-24 h-24 rounded-full object-cover border-4 border-[#00a9a9]"/><h2 className="text-2xl font-bold text-gray-800">{member.name}</h2><p className="text-gray-500">Nacimiento: {member.dob}</p></div><div className="bg-white p-4 rounded-lg shadow-sm border text-gray-700"><h3 className="font-bold text-[#00a9a9] mb-2">Información Médica</h3><p><strong>Grupo Sanguíneo:</strong> {member.bloodType || 'No especificado'}</p><p><strong>Alergias:</strong> {member.allergies || 'Ninguna conocida'}</p><p><strong>Medicación Actual:</strong> {member.medications || 'Ninguna'}</p></div><div className="bg-white p-4 rounded-lg shadow-sm border"><h3 className="font-bold text-[#00a9a9] mb-3">Calendario de Vacunación</h3><div className="space-y-3 text-gray-700">{calendario.map(item => (<div key={item.edad} className="flex items-start"><div className="w-1/4 font-semibold text-gray-600">{item.edad}</div><div className="w-3/4">{item.vacunas.map(v => <p key={v}>{v}</p>)}</div></div>))}<p className="text-xs text-gray-500 mt-4">*Vacunas no financiadas en todas las CCAA. Consulta con tu pediatra.</p></div></div></div></div>
);

const ProfileForm: FC<{ onSave: (member: Member) => void; onBack: () => void; memberToEdit: Member | null }> = ({ onSave, onBack, memberToEdit }) => {
    const [member, setMember] = useState<Omit<Member, "id">>(memberToEdit || { name: '', dob: '', bloodType: '', allergies: '', medications: '', photo: '' });
    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setMember(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); onSave({ ...member, id: memberToEdit?.id || Date.now() }); };

    return (
        <div><Header title={memberToEdit ? 'Editar Perfil' : 'Añadir Perfil'} onBack={onBack} /><form onSubmit={handleSubmit} className="p-4 space-y-4"><div className="flex flex-col"><label className="mb-1 text-gray-600 font-medium">Nombre Completo</label><input type="text" name="name" value={member.name} onChange={handleChange} required className="p-2 bg-gray-100 rounded-lg border border-gray-300 focus:border-[#00a9a9] focus:ring-[#00a9a9] outline-none text-gray-800" /></div><div className="flex flex-col"><label className="mb-1 text-gray-600 font-medium">Fecha de Nacimiento</label><input type="date" name="dob" value={member.dob} onChange={handleChange} required className="p-2 bg-gray-100 rounded-lg border border-gray-300 focus:border-[#00a9a9] focus:ring-[#00a9a9] outline-none text-gray-800" /></div><div className="flex flex-col"><label className="mb-1 text-gray-600 font-medium">URL de la Foto</label><input type="text" name="photo" value={member.photo} onChange={handleChange} className="p-2 bg-gray-100 rounded-lg border border-gray-300 focus:border-[#00a9a9] focus:ring-[#00a9a9] outline-none text-gray-800" /></div><div className="flex flex-col"><label className="mb-1 text-gray-600 font-medium">Grupo Sanguíneo</label><input type="text" name="bloodType" value={member.bloodType} onChange={handleChange} className="p-2 bg-gray-100 rounded-lg border border-gray-300 focus:border-[#00a9a9] focus:ring-[#00a9a9] outline-none text-gray-800" /></div><div className="flex flex-col"><label className="mb-1 text-gray-600 font-medium">Alergias</label><textarea name="allergies" value={member.allergies} onChange={handleChange} className="p-2 bg-gray-100 rounded-lg border border-gray-300 focus:border-[#00a9a9] focus:ring-[#00a9a9] outline-none text-gray-800" /></div><div className="flex flex-col"><label className="mb-1 text-gray-600 font-medium">Medicación Actual</label><textarea name="medications" value={member.medications} onChange={handleChange} className="p-2 bg-gray-100 rounded-lg border border-gray-300 focus:border-[#00a9a9] focus:ring-[#00a9a9] outline-none text-gray-800" /></div><button type="submit" className="w-full p-4 bg-[#00a9a9] hover:bg-[#008f8f] text-white rounded-xl font-bold text-lg transition-colors shadow-lg">Guardar</button></form></div>
    );
};

const AIAssistantScreen: FC<{ onBack: () => void; queryContext: string; setQueryContext: (context: string) => void; familyMembers: Member[] }> = ({ onBack, queryContext, setQueryContext, familyMembers }) => {
    const [prompt, setPrompt] = useState<string>(queryContext);
    const [response, setResponse] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    const calculateAge = (dob: string): number => {
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    useEffect(() => {
        if (queryContext) {
            setQueryContext('');
        }
    }, [queryContext, setQueryContext]);

    const handleAskAI = async () => {
        if (!prompt) return;
        const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
        if (!apiKey) {
            setError("Error de configuración: La clave de API de Gemini no está definida. Sigue la guía para añadirla en Vercel.");
            return;
        }
        setIsLoading(true); setError(''); setResponse('');

        let contextPrompt = "";
        const lowerCasePrompt = prompt.toLowerCase();

        familyMembers.forEach(member => {
            if (lowerCasePrompt.includes(member.name.toLowerCase())) {
                const age = calculateAge(member.dob);
                contextPrompt += ` Ten en cuenta el siguiente contexto sobre ${member.name}: es una persona de ${age} años. Alergias conocidas: ${member.allergies || 'ninguna'}. Medicación actual: ${member.medications || 'ninguna'}.`;
            }
        });

        const fullPrompt = `Actúa como un asistente de primeros auxilios. Tu respuesta debe ser clara, concisa y en un formato fácil de leer (usa negritas y listas si es necesario). Proporciona información útil y segura.${contextPrompt} Al final de CADA respuesta, incluye la siguiente advertencia OBLIGATORIA: "AVISO: Esta información es generada por IA y no reemplaza el consejo médico profesional. En caso de una emergencia real, llama al 112 o acude a un centro médico.". Pregunta del usuario: "${prompt}"`;
        const payload = { contents: [{ role: "user", parts: [{ text: fullPrompt }] }] };
        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

        try {
            const res = await fetch(apiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (!res.ok) throw new Error(`Error en la API: ${res.statusText}`);
            const data = await res.json();
            if (data.candidates?.[0]?.content?.parts?.[0]?.text) {
                const text = data.candidates[0].content.parts[0].text;
                const formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br />');
                setResponse(formattedText);
            } else { throw new Error("No se recibió una respuesta válida del asistente."); }
        } catch (e: any) {
            setError(e.message || "Ocurrió un error al contactar al asistente.");
            console.error(e);
        } finally { setIsLoading(false); }
    };

    return (
        <div><Header title="Asistente IA" onBack={onBack} /><div className="p-4 space-y-4"><div className="bg-[#ffeded] text-gray-700 p-4 rounded-lg"><h3 className="font-bold">Consulta al Asistente Virtual</h3><p className="text-sm mt-1">Puedes incluir el nombre de un miembro de tu familia en la consulta.</p></div><div className="flex space-x-2"><input type="text" value={prompt} onChange={(e: ChangeEvent<HTMLInputElement>) => setPrompt(e.target.value)} placeholder="Ej: '¿Qué hago si Rodrigo tiene fiebre?'" className="flex-grow p-3 bg-gray-100 rounded-lg border border-gray-300 focus:border-[#00a9a9] focus:ring-[#00a9a9] outline-none text-gray-800" onKeyPress={(e: KeyboardEvent) => e.key === 'Enter' && handleAskAI()} /><button onClick={handleAskAI} disabled={isLoading} className="p-3 bg-[#00a9a9] text-white rounded-lg disabled:bg-[#008f8f]"><Send /></button></div>{isLoading && <div className="text-center p-4">Cargando respuesta...</div>}{error && <div className="text-center p-4 text-red-500">{error}</div>}{response && (<div className="bg-white border p-4 rounded-lg space-y-2 text-gray-700" dangerouslySetInnerHTML={{ __html: response }}></div>)}</div></div>
    );
};

const FirstAidKitScreen: FC<{ onBack: () => void }> = ({ onBack }) => {
    const [kitItems, setKitItems] = useState<FirstAidKitItem[]>([]);

    useEffect(() => {
        const savedKit = localStorage.getItem('firstAidKit');
        setKitItems(savedKit ? JSON.parse(savedKit) : initialFirstAidKit);
    }, []);

    const handleToggleItem = (index: number) => {
        const newKitItems = [...kitItems];
        newKitItems[index].checked = !newKitItems[index].checked;
        setKitItems(newKitItems);
        localStorage.setItem('firstAidKit', JSON.stringify(newKitItems));
    };

    return (
        <div>
            <Header title="Checklist del Botiquín" onBack={onBack} />
            <div className="p-4 space-y-3">
                {kitItems.map((item, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg shadow-sm border flex items-center justify-between">
                        <div className="flex items-center">
                            <input type="checkbox" checked={item.checked} onChange={() => handleToggleItem(index)} className="h-6 w-6 rounded border-gray-300 text-[#00a9a9] focus:ring-[#00a9a9]" />
                            <div className="ml-4">
                                <p className={`font-semibold ${item.checked ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{item.name}</p>
                                <p className={`text-sm ${item.checked ? 'text-gray-400' : 'text-gray-600'}`}>{item.description}</p>
                            </div>
                        </div>
                        {!item.checked && (
                            <a href={`https://www.amazon.es/s?k=${encodeURIComponent(item.name)}&tag=paraelbebe-21`} target="_blank" rel="noopener noreferrer" className="ml-4 p-2 bg-amber-400 text-amber-900 rounded-full hover:bg-amber-500 transition-colors">
                                <ShoppingCart className="h-5 w-5" />
                            </a>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const SymptomCheckerScreen: FC<{ onBack: () => void; familyMembers: Member[] }> = ({ onBack, familyMembers }) => {
    const [symptoms, setSymptoms] = useState('');
    const [response, setResponse] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const calculateAge = (dob: string): number => {
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const handleAnalyze = async () => {
        if (!symptoms) return;
        const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
        if (!apiKey) {
            setError("Error de configuración: La clave de API de Gemini no está definida.");
            return;
        }
        setIsLoading(true); setError(''); setResponse('');

        let contextPrompt = "";
        const lowerCaseSymptoms = symptoms.toLowerCase();
        familyMembers.forEach(member => {
            if (lowerCaseSymptoms.includes(member.name.toLowerCase())) {
                const age = calculateAge(member.dob);
                contextPrompt += ` La consulta es sobre ${member.name}, que tiene ${age} años.`;
            }
        });

        const fullPrompt = `Actúa como un asistente pediátrico virtual. Dados los siguientes síntomas: "${symptoms}".${contextPrompt} Describe brevemente 2 o 3 posibles causas comunes en un lenguaje sencillo y no alarmista. No des un diagnóstico. Termina siempre indicando claramente bajo qué circunstancias específicas (señales de alarma) se debe contactar con un médico o acudir a urgencias. Finaliza con la advertencia obligatoria: "AVISO: Esto es una orientación y no reemplaza un diagnóstico médico profesional."`;
        
        const payload = { contents: [{ role: "user", parts: [{ text: fullPrompt }] }] };
        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

        try {
            const res = await fetch(apiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (!res.ok) throw new Error(`Error en la API: ${res.statusText}`);
            const data = await res.json();
            if (data.candidates?.[0]?.content?.parts?.[0]?.text) {
                const text = data.candidates[0].content.parts[0].text;
                const formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br />');
                setResponse(formattedText);
            } else { throw new Error("No se recibió una respuesta válida."); }
        } catch (e: any) {
            setError(e.message || "Ocurrió un error al analizar los síntomas.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <Header title="Analizador de Síntomas" onBack={onBack} />
            <div className="p-4 space-y-4">
                <div className="bg-[#ffeded] text-gray-700 p-4 rounded-lg">
                    <h3 className="font-bold">Orientación sobre Síntomas</h3>
                    <p className="text-sm mt-1">Describe los síntomas de forma sencilla para recibir una orientación. (Ej: "Mi hijo tiene fiebre y tos seca").</p>
                </div>
                <textarea value={symptoms} onChange={(e) => setSymptoms(e.target.value)} placeholder="Describe los síntomas aquí..." rows={4} className="w-full p-3 bg-gray-100 rounded-lg border border-gray-300 focus:border-[#00a9a9] focus:ring-[#00a9a9] outline-none text-gray-800" />
                <button onClick={handleAnalyze} disabled={isLoading} className="w-full p-4 bg-[#00a9a9] hover:bg-[#008f8f] text-white rounded-xl font-bold text-lg transition-colors shadow-lg disabled:bg-[#008f8f]">
                    {isLoading ? <Loader className="animate-spin mx-auto" /> : 'Analizar Síntomas'}
                </button>
                {error && <div className="text-center p-4 text-red-500">{error}</div>}
                {response && (<div className="bg-white border p-4 rounded-lg space-y-2 text-gray-700" dangerouslySetInnerHTML={{ __html: response }}></div>)}
            </div>
        </div>
    );
};

const ProspectusInterpreterScreen: FC<{ onBack: () => void }> = ({ onBack }) => {
    const [prospectusText, setProspectusText] = useState('');
    const [response, setResponse] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [weight, setWeight] = useState('');

    const handleAnalyze = async (question: string) => {
        if (!prospectusText) {
            setError("Por favor, pega el texto del prospecto primero.");
            return;
        }
        let fullQuestion = question;
        if (question.includes("[X]")) {
            if (!weight) {
                setError("Por favor, introduce el peso del niño.");
                return;
            }
            fullQuestion = question.replace("[X]", weight);
        }

        const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
        if (!apiKey) {
            setError("Error de configuración: La clave de API de Gemini no está definida.");
            return;
        }
        setIsLoading(true); setError(''); setResponse('');

        const fullPrompt = `Actúa como un asistente farmacéutico. Analiza el siguiente texto de un prospecto y responde de forma clara y concisa a la pregunta del usuario. Prospecto: "${prospectusText}". Pregunta: "${fullQuestion}". Finaliza siempre con la advertencia: "AVISO: Esta es una interpretación de la IA. Consulta siempre a tu médico o farmacéutico antes de administrar cualquier medicamento."`;
        
        const payload = { contents: [{ role: "user", parts: [{ text: fullPrompt }] }] };
        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

        try {
            const res = await fetch(apiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (!res.ok) throw new Error(`Error en la API: ${res.statusText}`);
            const data = await res.json();
            if (data.candidates?.[0]?.content?.parts?.[0]?.text) {
                const text = data.candidates[0].content.parts[0].text;
                const formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br />');
                setResponse(formattedText);
            } else { throw new Error("No se recibió una respuesta válida."); }
        } catch (e: any) {
            setError(e.message || "Ocurrió un error al analizar el prospecto.");
        } finally {
            setIsLoading(false);
        }
    };
    
    return (
        <div>
            <Header title="Intérprete de Prospectos" onBack={onBack} />
            <div className="p-4 space-y-4">
                <div className="bg-[#ffeded] text-gray-700 p-4 rounded-lg">
                    <h3 className="font-bold">"Traduce" un Prospecto</h3>
                    <p className="text-sm mt-1">Pega el texto de un prospecto y haz clic en una de las preguntas para obtener una respuesta sencilla.</p>
                </div>
                <textarea value={prospectusText} onChange={(e) => setProspectusText(e.target.value)} placeholder="Pega aquí el texto del prospecto..." rows={8} className="w-full p-3 bg-gray-100 rounded-lg border border-gray-300 focus:border-[#00a9a9] focus:ring-[#00a9a9] outline-none text-gray-800" />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <button onClick={() => handleAnalyze("¿Para qué sirve este medicamento?")} disabled={isLoading} className="w-full p-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-medium transition-colors text-sm">¿Para qué sirve?</button>
                    <button onClick={() => handleAnalyze("Resume los efectos secundarios más comunes")} disabled={isLoading} className="w-full p-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-medium transition-colors text-sm">Efectos secundarios</button>
                </div>
                 <div className="flex items-center gap-2">
                    <input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="Peso del niño (kg)" className="flex-grow p-3 bg-gray-100 rounded-lg border border-gray-300 focus:border-[#00a9a9] focus:ring-[#00a9a9] outline-none text-gray-800" />
                    <button onClick={() => handleAnalyze("Calcula la dosis para un niño de [X] kg")} disabled={isLoading || !weight} className="p-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-medium transition-colors text-sm disabled:opacity-50">Calcular dosis</button>
                </div>

                {isLoading && <div className="text-center p-4"><Loader className="animate-spin mx-auto text-[#00a9a9]" /></div>}
                {error && <div className="text-center p-4 text-red-500">{error}</div>}
                {response && (<div className="bg-white border p-4 rounded-lg space-y-2 text-gray-700" dangerouslySetInnerHTML={{ __html: response }}></div>)}
            </div>
        </div>
    );
};

const MealPlannerScreen: FC<{ onBack: () => void }> = ({ onBack }) => {
    const [age, setAge] = useState('');
    const [allergies, setAllergies] = useState('');
    const [preferences, setPreferences] = useState('');
    const [menu, setMenu] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleGenerateMenu = async () => {
        if (!age) {
            setError("Por favor, introduce la edad del niño.");
            return;
        }
        const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
        if (!apiKey) {
            setError("Error de configuración: La clave de API de Gemini no está definida.");
            return;
        }
        setIsLoading(true); setError(''); setMenu('');

        const fullPrompt = `Crea un plan de menú semanal (lunes a domingo, con desayuno, comida y cena) para un niño de ${age} años. Ten en cuenta estas restricciones: Alergias: ${allergies || 'ninguna'}. Preferencias: ${preferences || 'ninguna'}. **Todas las recetas deben ser adaptables o específicas para el robot de cocina Thermomix**. Devuelve el resultado únicamente como una tabla Markdown con las columnas: "Día", "Desayuno", "Comida", "Cena". No incluyas ningún otro texto o introducción.`;
        
        const payload = { contents: [{ role: "user", parts: [{ text: fullPrompt }] }] };
        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

        try {
            const res = await fetch(apiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (!res.ok) throw new Error(`Error en la API: ${res.statusText}`);
            const data = await res.json();
            if (data.candidates?.[0]?.content?.parts?.[0]?.text) {
                const text = data.candidates[0].content.parts[0].text;
                setMenu(text);
            } else { throw new Error("No se recibió una respuesta válida."); }
        } catch (e: any) {
            setError(e.message || "Ocurrió un error al generar el menú.");
        } finally {
            setIsLoading(false);
        }
    };

    const parseMarkdownTable = (markdown: string) => {
        const rows = markdown.trim().split('\n').map(row => row.split('|').map(cell => cell.trim()));
        if (rows.length < 2) return null;
        const headers = rows[0];
        const data = rows.slice(2); // Omitir la línea de separación '---'
        
        return (
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border rounded-lg">
                    <thead className="bg-gray-50">
                        <tr>
                            {headers.map((header, i) => <th key={i} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{header}</th>)}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {data.map((row, i) => (
                            <tr key={i}>
                                {row.map((cell, j) => <td key={j} className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{cell}</td>)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div>
            <Header title="Planificador de Menús" onBack={onBack} />
            <div className="p-4 space-y-4">
                <div className="bg-[#ffeded] text-gray-700 p-4 rounded-lg">
                    <h3 className="font-bold">Genera un Menú Semanal</h3>
                    <p className="text-sm mt-1">Introduce los datos para crear un menú saludable y equilibrado con recetas para Thermomix.</p>
                </div>
                <div className="space-y-3">
                    <input type="number" value={age} onChange={(e) => setAge(e.target.value)} placeholder="Edad del niño (ej: 4)" className="w-full p-3 bg-gray-100 rounded-lg border border-gray-300 focus:border-[#00a9a9] focus:ring-[#00a9a9] outline-none text-gray-800" />
                    <input type="text" value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="Alergias o intolerancias (ej: lactosa, frutos secos)" className="w-full p-3 bg-gray-100 rounded-lg border border-gray-300 focus:border-[#00a9a9] focus:ring-[#00a9a9] outline-none text-gray-800" />
                    <input type="text" value={preferences} onChange={(e) => setPreferences(e.target.value)} placeholder="Preferencias (ej: más verdura, sin pescado)" className="w-full p-3 bg-gray-100 rounded-lg border border-gray-300 focus:border-[#00a9a9] focus:ring-[#00a9a9] outline-none text-gray-800" />
                </div>
                <button onClick={handleGenerateMenu} disabled={isLoading} className="w-full p-4 bg-[#00a9a9] hover:bg-[#008f8f] text-white rounded-xl font-bold text-lg transition-colors shadow-lg disabled:bg-[#008f8f]">
                    {isLoading ? <Loader className="animate-spin mx-auto" /> : 'Generar Menú'}
                </button>
                {error && <div className="text-center p-4 text-red-500">{error}</div>}
                {menu && (
                    <div className="bg-white border p-4 rounded-lg space-y-2 text-gray-700">
                        {parseMarkdownTable(menu)}
                    </div>
                )}
            </div>
        </div>
    );
};


// --- APP PRINCIPAL ---
const App: FC = () => {
  const [screen, setScreen] = useState<Screen>('home');
  const [selectedGuide, setSelectedGuide] = useState<Guide | null>(null);
  const [selectedSubsection, setSelectedSubsection] = useState<{title: string, content: Subsection} | null>(null);
  const [familyMembers, setFamilyMembers] = useState<Member[]>([]);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [aiQueryContext, setAiQueryContext] = useState<string>('');

  useEffect(() => {
    try {
        const storedMembers = localStorage.getItem('familyMembers');
        if (storedMembers) setFamilyMembers(JSON.parse(storedMembers));
    } catch (error) { console.error("Error al cargar datos:", error); setFamilyMembers([]); }
  }, []);

  useEffect(() => {
    try { localStorage.setItem('familyMembers', JSON.stringify(familyMembers)); } catch (error) { console.error("Error al guardar datos:", error); }
  }, [familyMembers]);

  const handleSelectGuide = (guide: Guide) => {
    if (guide.interactive) {
        setSelectedGuide(guide);
    } else if (guide.content){
        setSelectedSubsection({ title: guide.title, content: guide.content });
    }
  };

  const handleSelectSubsection = (subsection: Subsection, title: string) => {
    setSelectedSubsection({ title, content: subsection });
  };
  
  const handleSaveMember = (memberData: Member) => {
    setFamilyMembers(prev => {
        const exists = prev.some(m => m.id === memberData.id);
        if (exists) {
            return prev.map(m => m.id === memberData.id ? memberData : m);
        }
        return [...prev, memberData];
    });
    setEditingMember(null); setScreen('profiles');
  };
  
  const handleEditMember = (member: Member) => { setEditingMember(member); setScreen('editProfile'); }
  
  const handleAmplifyWithAI = (context: string) => {
    setAiQueryContext(`Ampliar información sobre: ${context}`);
    setScreen('aiAssistant');
  };

  const navigate = (screenName: Screen) => { 
      setScreen(screenName); 
      setSelectedGuide(null); 
      setSelectedMember(null); 
      setEditingMember(null); 
      setSelectedSubsection(null);
      setAiQueryContext('');
  };

  const renderContent = () => {
    if (selectedSubsection) {
        return <GuideDetailScreen title={selectedSubsection.title} content={selectedSubsection.content} onBack={() => {setSelectedSubsection(null)}} onAmplifyWithAI={handleAmplifyWithAI} />;
    }
    if (selectedGuide) {
        return <SubsectionsScreen guide={selectedGuide} onSelect={handleSelectSubsection} onBack={() => setSelectedGuide(null)} />;
    }
    if (selectedMember) {
        return <ProfileDetailScreen member={selectedMember} onBack={() => setSelectedMember(null)} calendario={calendarioVacunasEspaña} />;
    }
    
    switch (screen) {
      case 'home': return <HomeScreen navigate={navigate} />;
      case 'emergency': return <GuideListScreen title="Guías de Emergencia" guides={guiasEmergencia} isLoading={false} onSelect={handleSelectGuide} onBack={() => navigate('home')} />;
      case 'firstAid': return <GuideListScreen title="Primeros Auxilios" guides={guiasPrimerosAuxilios} isLoading={false} onSelect={handleSelectGuide} onBack={() => navigate('home')} />;
      case 'profiles': return <ProfilesScreen members={familyMembers} onSelect={setSelectedMember} onAdd={() => navigate('editProfile')} onEdit={handleEditMember} onBack={() => navigate('home')} />;
      case 'editProfile': return <ProfileForm onSave={handleSaveMember} onBack={() => navigate('profiles')} memberToEdit={editingMember} />;
      case 'aiAssistant': return <AIAssistantScreen onBack={() => navigate('home')} queryContext={aiQueryContext} setQueryContext={setAiQueryContext} familyMembers={familyMembers} />;
      case 'firstAidKit': return <FirstAidKitScreen onBack={() => navigate('home')} />;
      case 'symptomChecker': return <SymptomCheckerScreen onBack={() => navigate('home')} familyMembers={familyMembers} />;
      case 'prospectusInterpreter': return <ProspectusInterpreterScreen onBack={() => navigate('home')} />;
      case 'mealPlanner': return <MealPlannerScreen onBack={() => navigate('home')} />;
    }
  };

  return (
    <div className="bg-gray-100 text-gray-800 min-h-screen font-sans pb-20">
      <main>{renderContent()}</main>
      <BottomNav screen={screen} setScreen={navigate} />
    </div>
  );
}

export default App;
